import { ResponseStatus } from '@revenuemarket/http';
import { convert } from '@revenuemarket/utils';
import detectPlatform, { detectBrowser } from '@revenuemarket/utils/detectPlatform';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import { useRouter } from 'next/router';

import { createDemoTrialToken } from '~apis/auth/demoTrial';
import { changeEmail, requestEmailPassCode, verifyEmailChange } from '~apis/auth/email';
import getAgreements from '~apis/auth/getAgreements';
import getUser from '~apis/auth/getUser';
import joinWorkspaceByInvitation from '~apis/auth/joinWorkspaceByInvitation';
import maybeSignInWithOAuth, { OAuthProviderType } from '~apis/auth/maybeSignInWithOAuth';
import postPassCode from '~apis/auth/postPassCode';
import postPasswordVerifyPassCode from '~apis/auth/postPasswordVerifyPassCode';
import postRequestResetPasscode from '~apis/auth/postRequestResetPasscode';
import postResetPassword from '~apis/auth/postResetPassword';
import postUser from '~apis/auth/postUser';
import postVerifyPassCode from '~apis/auth/postVerifyPassCode';
import signUpByInvitation from '~apis/auth/signUpByInvitation';
import { deleteSuperToken, signIn, signInByMagicLinkToken, signOut } from '~apis/auth/signin';
import verifyJoinWorkspaceByInvitation from '~apis/auth/verifyJoinWorkspaceByInvitation';
import { getDomainForCookie } from '~apis/axios/instance';
import { getMetaData } from '~apis/metaData';
import { getAcquisitionChannel, postAcquisitionChannel } from '~apis/user';
import { useCompanyContext } from '~contexts/CompanyContext';
import { useCompanyId } from '~hooks/useCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { queryClient } from '~libs/queryClient';
import { mixpanel } from '~pages/_app';
import { deserializeAccessToken } from '~utils/auth';
import getTargetCompany from '~utils/auth/getTargetCompany';

import { CompaniesQueryKey } from '../companies/query';
import { UserCompaniesQueryKey } from '../users/query';

export const AuthQueryKey = {
  default: () => ['auth'],
  session: () => [...AuthQueryKey.default(), 'session'],
  agreements: () => [...AuthQueryKey.default(), 'agreements'],
  acquisitionChannel: () => [...AuthQueryKey.default(), 'acquisitionChannel'],
  user: ({ userId }: { userId: string }) => [...AuthQueryKey.default(), 'user', userId],
};

export function useSession() {
  return useQuery({
    queryKey: AuthQueryKey.session(),
    queryFn: async () => {
      const superToken = convert(getCookie('access_super_token'), String);
      const accessToken = convert(getCookie('accessToken'), String);
      if (superToken == null && accessToken == null) return null;
      const token = (superToken || accessToken) as string;
      const decodedToken = deserializeAccessToken(token);
      return {
        ...decodedToken,
        isAdmin: superToken != null,
      };
    },

    suspense: true,
  });
}

export function useSignIn(callbackUrl?: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signIn,
    async onSuccess(response) {
      if (response.Status === ResponseStatus.SUCCESS) {
        deleteSuperToken();
        const userAgent = typeof navigator === 'undefined' ? undefined : navigator.userAgent;
        const { accessToken } = response.Data?.data ?? {};
        if (!accessToken) return;
        setCookie('accessToken', accessToken, {
          // 30일
          expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          domain: getDomainForCookie(),
        });
        const session = deserializeAccessToken(accessToken);
        const companyId = await getTargetCompany(session.userId);

        await queryClient.invalidateQueries(AuthQueryKey.session());
        if (!companyId) return;
        const data = await getMetaData(companyId, session.userId, accessToken);
        const metaData = data?.Data;
        mixpanel.track(MIXPANEL_EVENT.USER.SIGN_IN, {
          distinct_id: session.userId,
          browser: convert(userAgent, detectBrowser),
          platform: convert(userAgent, detectPlatform),
          tradesCount: metaData?.data?.company.numOfTrades,
          tradeStatus: metaData?.data?.company.tradeStatus,
          checkingAccountsCount: metaData?.data?.company.numOfCheckingAccounts,
          banksCount: metaData?.data?.company.numOfBanks,
          certificatesCount: metaData?.data?.company.numOfCertificates,
          dqBank: metaData?.data?.company.dataQualifications?.bank,
          method: 'email',
        });
      }
    },
  });
}

export function useSignInWithOAuth(providerType: OAuthProviderType) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: maybeSignInWithOAuth,
    async onSuccess(response) {
      if (response.Status === ResponseStatus.SUCCESS && response.Data?.data?.signIn) {
        deleteSuperToken();
        if (response.Data?.data?.signIn) {
          const userAgent = typeof navigator === 'undefined' ? undefined : navigator.userAgent;
          const { token } = response?.Data?.data.signIn;
          const accessToken = token;
          setCookie('accessToken', accessToken, {
            // 30일
            expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
            domain: getDomainForCookie(),
          });
          const session = deserializeAccessToken(accessToken);
          const companyId = await getTargetCompany(session.userId);

          await queryClient.invalidateQueries(AuthQueryKey.session());
          if (!companyId) return;
          const data = await getMetaData(companyId, session.userId, accessToken);
          const metaData = data?.Data;
          mixpanel.track(MIXPANEL_EVENT.USER.SIGN_IN, {
            distinct_id: session.userId,
            browser: convert(userAgent, detectBrowser),
            platform: convert(userAgent, detectPlatform),
            tradesCount: metaData?.data?.company.numOfTrades,
            tradeStatus: metaData?.data?.company.tradeStatus,
            checkingAccountsCount: metaData?.data?.company.numOfCheckingAccounts,
            banksCount: metaData?.data?.company.numOfBanks,
            certificatesCount: metaData?.data?.company.numOfCertificates,
            dqBank: metaData?.data?.company.dataQualifications?.bank,
            method: providerType.toLowerCase(),
          });
        }
      }
    },
  });
}

export function useSignOut(callbackUrl?: string, preventRedirect?: boolean) {
  const queryClient = useQueryClient();
  const { setCompanyId } = useCompanyContext();
  const router = useRouter();
  return useMutation({
    mutationFn: signOut,
    async onSuccess() {
      await sessionStorage.removeItem('signupSteps');
      await queryClient.invalidateQueries(AuthQueryKey.session());
      await setCompanyId();
      if (preventRedirect === true) {
        router.reload();
      } else {
        await router.replace(`/auth/signin${callbackUrl ? `?callbackUrl=${callbackUrl}` : ''}`);
      }
    },
  });
}

export function useSignUp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postUser,
    async onSuccess(response) {
      if (response.Status === ResponseStatus.SUCCESS) {
        deleteSuperToken();
        const { accessToken } = response?.Data?.data ?? {};
        if (!accessToken) return;
        setCookie('accessToken', accessToken, {
          // 30일
          expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          domain: getDomainForCookie(),
        });
        await queryClient.invalidateQueries(AuthQueryKey.session());
      }
    },
  });
}

export function useJoinWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signUpByInvitation,
    async onSuccess(response) {
      if (response.Status === ResponseStatus.SUCCESS) {
        deleteSuperToken();
        const userAgent = typeof navigator === 'undefined' ? undefined : navigator.userAgent;
        const { accessToken } = response?.Data?.data ?? {};
        if (!accessToken) return;
        setCookie('accessToken', accessToken, {
          // 30일
          expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          domain: getDomainForCookie(),
        });
        await queryClient.invalidateQueries(AuthQueryKey.session());
        const session = deserializeAccessToken(accessToken);
        const companyId = await getTargetCompany(session.userId);
        if (!companyId) return;
        const data = await getMetaData(companyId, session.userId, accessToken);

        const metaData = data?.Data;

        mixpanel.track(MIXPANEL_EVENT.USER.JOIN_WORKSPACE, {
          distinct_id: session.userId,
          browser: convert(userAgent, detectBrowser),
          platform: convert(userAgent, detectPlatform),
          tradesCount: metaData?.data?.company.numOfTrades,
          tradeStatus: metaData?.data?.company.tradeStatus,
          checkingAccountsCount: metaData?.data?.company.numOfCheckingAccounts,
          banksCount: metaData?.data?.company.numOfBanks,
          certificatesCount: metaData?.data?.company.numOfCertificates,
          dqBank: metaData?.data?.company.dataQualifications?.bank,
        });
      }
    },
  });
}

export function useVerifyJoinWorkspace(invitationToken: string) {
  return useQuery({
    queryFn: () => verifyJoinWorkspaceByInvitation(invitationToken),
    enabled: !!invitationToken,
  });
}

export function useExistUserJoinWorkspace() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: joinWorkspaceByInvitation,
    async onSuccess(response) {
      if (response.Status === ResponseStatus.SUCCESS) {
        deleteCookie('access_super_token', {
          path: '/',
          domain: `${window.location.host}`,
        });

        const { accessToken } = response?.Data?.data ?? {};
        if (!accessToken) return;
        setCookie('accessToken', accessToken, {
          // 30일
          expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          domain: getDomainForCookie(),
        });
        await queryClient.invalidateQueries(AuthQueryKey.session());
        await queryClient.invalidateQueries(UserCompaniesQueryKey.default());
      }
    },
  });
}

export function useAgreements() {
  return useQuery({
    queryKey: AuthQueryKey.agreements(),
    queryFn: () => getAgreements(),
  });
}

export function useSendPasscode() {
  return useMutation({
    mutationFn: postPassCode,
  });
}

export function useVerifyPasscode() {
  return useMutation({
    mutationFn: postVerifyPassCode,
  });
}

export function useSendResetPasswordPasscode() {
  return useMutation({
    mutationFn: postRequestResetPasscode,
  });
}

export function useVerifyResetPasswordPasscode() {
  return useMutation({
    mutationFn: postPasswordVerifyPassCode,
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: postResetPassword,
  });
}

export function usePostAcquisitionChannel() {
  return useMutation({
    mutationFn: postAcquisitionChannel,
  });
}

export function useGetAcquisitionChannel(userId: string) {
  return useQuery({
    queryKey: AuthQueryKey.acquisitionChannel(),
    queryFn: () => getAcquisitionChannel(userId),
    enabled: !!userId,
  });
}

export function useRequestEmailPassCode() {
  return useMutation({
    mutationFn: requestEmailPassCode,
  });
}

export function useVerifyEmailChange() {
  return useMutation({
    mutationFn: verifyEmailChange,
  });
}

export function useChangeEmail() {
  const { companyId } = useCompanyId();
  return useMutation({
    mutationFn: changeEmail,
    onSuccess: async (response, variables) => {
      await queryClient.invalidateQueries(
        CompaniesQueryKey.member({
          companyId,
          memberId: variables.userId,
        }),
      );
    },
  });
}

export function useSignInByMagicLinkToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signInByMagicLinkToken,
    async onSuccess(response) {
      if (response.Status === ResponseStatus.SUCCESS) {
        deleteSuperToken();
        const userAgent = typeof navigator === 'undefined' ? undefined : navigator.userAgent;
        const { accessToken } = response?.Data?.data ?? {};
        if (!accessToken) return;
        setCookie('accessToken', accessToken, {
          // 30일
          expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          domain: getDomainForCookie(),
        });
        const session = deserializeAccessToken(accessToken);
        const companyId = await getTargetCompany(session.userId);
        if (!companyId) return;

        await queryClient.invalidateQueries(AuthQueryKey.session());

        const data = await getMetaData(companyId, session.userId, accessToken);
        const metaData = data?.Data;
        mixpanel.track(MIXPANEL_EVENT.USER.SIGN_IN, {
          distinct_id: session.userId,
          browser: convert(userAgent, detectBrowser),
          platform: convert(userAgent, detectPlatform),
          tradesCount: metaData?.data?.company.numOfTrades,
          tradeStatus: metaData?.data?.company.tradeStatus,
          checkingAccountsCount: metaData?.data?.company.numOfCheckingAccounts,
          banksCount: metaData?.data?.company.numOfBanks,
          certificatesCount: metaData?.data?.company.numOfCertificates,
          dqBank: metaData?.data?.company.dataQualifications?.bank,
          method: 'magic-link',
        });
      }
    },
  });
}

export function useUser(userId: string) {
  return useQuery({
    queryKey: AuthQueryKey.user({ userId }),
    queryFn: () => getUser(userId),
    enabled: !!userId,
  });
}

export function useDemoTrialToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDemoTrialToken,
    async onSuccess(response) {
      if (response.Status === ResponseStatus.SUCCESS && response.Data?.data?.token) {
        const { token } = response?.Data?.data;

        setCookie('accessToken', token, {
          // 1시간
          expires: new Date(Date.now() + 60 * 60 * 1000),
          domain: getDomainForCookie(),
        });

        await queryClient.invalidateQueries(AuthQueryKey.session());
      }
    },
  });
}
